/**
 * Dispatcher Toggle
 * AIDLC Admin Portal
 *
 * On/off control for the Agent Dispatcher (bridge/agent-dispatcher.js), shown on the
 * Agent Dashboard beside the status filters.
 *
 * Why it is here and not only on the Dispatcher Control page: this one service decides
 * whether two things the dashboard is about actually happen. With it stopped, a chat
 * message to an agent is never answered, and no token usage is recorded at all, because
 * the dispatcher is the only writer of bridge/token-record.json. The Token Usage page
 * then reads zero while the agent cards on this page report millions, which looks like a
 * broken dashboard rather than a stopped service.
 *
 * Control is restricted the same way the API restricts it: POST /api/dispatcher/start and
 * /stop require Admin or Operator, and are refused altogether on a server that does not
 * run the dispatcher itself. Anyone who cannot use the switch sees the state and the
 * reason, rather than a control that will be refused.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import {
  getDispatcherState,
  startDispatcher,
  stopDispatcher,
  dispatcherErrorReason,
} from '../../services/dispatcherService';
import type { DispatcherStatus } from '../../types/dispatcher';
import { useAuth } from '../../hooks/useAuth';

const START_CONFIRMATION =
  'Start the chat service?\n\nAgents will then answer chat messages, and each reply is a billed Claude call.';

interface DispatcherToggleProps {
  /** The bridge socket, when the page has one. Live state arrives on 'dispatcher:state'. */
  socket: Socket | null;
}

export function DispatcherToggle({ socket }: DispatcherToggleProps) {
  const { isAdmin, isOperator } = useAuth();
  const hasRole = isAdmin || isOperator;

  const [status, setStatus] = useState<DispatcherStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // State updates land a render later, so two quick clicks would both see
  // busy === false. The ref is set in the same tick as the first click.
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Initial read. The socket carries changes after this, including ones made from
  // the Dispatcher Control page or by the supervisor adopting an already-running
  // dispatcher it does not own.
  useEffect(() => {
    let live = true;
    getDispatcherState()
      .then((s) => { if (live) setStatus(s); })
      .catch(() => { if (live) setError('Could not read the dispatcher state.'); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!socket) return;
    const onState = (s: DispatcherStatus) => setStatus(s);
    socket.on('dispatcher:state', onState);
    return () => { socket.off('dispatcher:state', onState); };
  }, [socket]);

  const running = status?.running === true;
  const managedHere = status?.managed === 'local';
  const canControl = hasRole && managedHere;

  const toggle = useCallback(async () => {
    if (inFlight.current || !canControl) return;
    if (!running && !window.confirm(START_CONFIRMATION)) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = running ? await stopDispatcher() : await startDispatcher();
      if (!result.ok) {
        setError(result.reason || (running ? 'The dispatcher did not stop.' : 'The dispatcher did not start.'));
      }
    } catch (err) {
      if (mounted.current) {
        setError(dispatcherErrorReason(err, running ? 'Stopping the dispatcher failed.' : 'Starting the dispatcher failed.'));
      }
    }
    // Read back after success and after failure alike: a refused start can mean
    // somebody else already started it, and the supervisor is the authority on
    // whether a process is actually up.
    try {
      const latest = await getDispatcherState();
      if (mounted.current) setStatus(latest);
    } catch {
      // The switch keeps its last known position; the error above still shows.
    }
    inFlight.current = false;
    if (mounted.current) setBusy(false);
  }, [canControl, running]);

  let stateLabel = 'Checking…';
  let explain = 'Reading the state of the chat service.';
  if (status && status.managed === 'remote') {
    stateLabel = 'Managed remotely';
    explain = status.managedReason ?? 'The dispatcher runs on another machine and is not controlled from here.';
  } else if (status && status.managed === 'disabled') {
    stateLabel = 'Not enabled here';
    explain = status.managedReason ?? 'Dispatcher control is not enabled on this server.';
  } else if (status) {
    stateLabel = running ? 'Running' : 'Stopped';
    explain = running
      ? 'Agents answer chat, and token usage is being recorded.'
      : 'Chat messages to agents go unanswered and no token usage is recorded while this is off.';
  }

  const dotClass = status === null || !managedHere ? 'bg-gray-300' : running ? 'bg-green-500' : 'bg-gray-400';

  return (
    <div className="flex items-center gap-2">
      {error && (
        <span role="alert" className="text-xs text-red-700">{error}</span>
      )}
      <div
        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-md"
        title={explain}
      >
        <span aria-hidden="true" className={`w-2 h-2 rounded-full ${dotClass}`} />
        <span className="text-xs font-medium text-gray-600">
          Chat service <span className={running ? 'text-green-700' : 'text-gray-500'}>{stateLabel}</span>
        </span>

        {canControl && (
          <button
            type="button"
            role="switch"
            aria-checked={running}
            aria-label={`Chat service: ${stateLabel}. ${explain}`}
            disabled={busy}
            onClick={toggle}
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 ${
              running ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <span
              aria-hidden="true"
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                running ? 'translate-x-[18px]' : 'translate-x-[2px]'
              }`}
            />
          </button>
        )}
        {status !== null && managedHere && !hasRole && (
          <span className="text-[11px] text-gray-400">Admin or Operator</span>
        )}
      </div>
    </div>
  );
}

export default DispatcherToggle;
